import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  CircularProgress,
  Box,
  TextField
} from '@mui/material';
import axios from 'axios';
import CronBuilder from './CronBuilder';

export default function EditScheduleModal({ open, onClose, onSuccess, onError, schedule }) {
  const [formData, setFormData] = useState({
    schedule_type: 'daily',
    cron_expression: '0 2 * * *',
    sync_window_days: 3,
    intraday_window_days: 0,
    is_enabled: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCustomWindow, setIsCustomWindow] = useState(false);

  const WINDOW_PRESETS = [3, 7, 14, 30];
  const WINDOW_MIN = 1;
  const WINDOW_MAX = 90;
  const INTRADAY_MIN = 0;

  // Initialize form with schedule data when modal opens
  // Use a ref to track if we've initialized to avoid dependency issues
  const initializedRef = React.useRef(false);

  useEffect(() => {
    // Initialize form when modal opens with schedule data
    if (schedule && open && !initializedRef.current) {
      console.log('=== EDIT MODAL: Initializing form with schedule data ===');
      console.log('Schedule object:', schedule);
      console.log('Schedule cron_expression:', schedule.cron_expression);
      
      const initialWindow = Number(schedule.sync_window_days) || 3;
      setFormData({
        schedule_type: schedule.schedule_type || 'daily',
        cron_expression: schedule.cron_expression || '0 2 * * *',
        sync_window_days: initialWindow,
        intraday_window_days: Number(schedule.intraday_window_days) || 0,
        is_enabled: schedule.is_enabled
      });
      setIsCustomWindow(!WINDOW_PRESETS.includes(initialWindow));
      initializedRef.current = true;
    }

    // Reset initialization flag when modal closes
    if (!open) {
      initializedRef.current = false;
    }
  }, [schedule, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const windowNum = Number(formData.sync_window_days);
    if (!Number.isInteger(windowNum) || windowNum < WINDOW_MIN || windowNum > WINDOW_MAX) {
      newErrors.sync_window_days = `Data window must be a whole number between ${WINDOW_MIN} and ${WINDOW_MAX} days`;
    }

    const intradayNum = Number(formData.intraday_window_days);
    if (!Number.isInteger(intradayNum) || intradayNum < INTRADAY_MIN || intradayNum > WINDOW_MAX) {
      newErrors.intraday_window_days = `Intraday window must be a whole number between ${INTRADAY_MIN} and ${WINDOW_MAX} days`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        ...formData,
        intraday_window_days: Number(formData.intraday_window_days) || 0
      };

      console.log('=== EDIT MODAL: Submitting schedule update ===');
      console.log('Schedule ID:', schedule.schedule_id);
      console.log('Form data being sent:', payload);
      console.log('Cron expression:', payload.cron_expression);

      const response = await axios.put(`/api/connectors/schedules/${schedule.schedule_id}`, payload);
      
      console.log('=== EDIT MODAL: Update response ===');
      console.log('Response:', response.data);
      
      onSuccess();
    } catch (error) {
      console.error('=== EDIT MODAL: Error updating schedule ===');
      console.error('Error:', error);
      console.error('Response data:', error.response?.data);
      onError(error.response?.data?.message || 'Failed to update schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    setIsCustomWindow(false);
    initializedRef.current = false;
    onClose();
  };

  const handleWindowSelectChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      setIsCustomWindow(true);
    } else {
      setIsCustomWindow(false);
      handleInputChange('sync_window_days', val);
    }
  };

  const handleCustomWindowChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      handleInputChange('sync_window_days', '');
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    handleInputChange('sync_window_days', parsed);
  };

  const handleIntradayWindowChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      handleInputChange('intraday_window_days', '');
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    handleInputChange('intraday_window_days', parsed);
  };

  if (!schedule) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Schedule for {schedule.connector_name}</DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Connector Info (Read-only) */}
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Connector:</strong> {schedule.connector_name}<br/>
                  <strong>Platform:</strong> {schedule.platform_name}<br/>
                  <strong>Brand:</strong> {schedule.brand_name || 'No Brand'}
                </Typography>
              </Alert>
            </Grid>

            {/* Schedule Configuration */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                When should this run?
              </Typography>
              <CronBuilder
                value={formData.cron_expression}
                onChange={(value) => handleInputChange('cron_expression', value)}
                error={errors.cron_expression}
              />
            </Grid>

            {/* Sync Window Days */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Data Window</InputLabel>
                <Select
                  value={isCustomWindow ? 'custom' : formData.sync_window_days}
                  label="Data Window"
                  onChange={handleWindowSelectChange}
                >
                  <MenuItem value={3}>3 Days (Recommended)</MenuItem>
                  <MenuItem value={7}>1 Week</MenuItem>
                  <MenuItem value={14}>2 Weeks</MenuItem>
                  <MenuItem value={30}>1 Month</MenuItem>
                  <MenuItem value="custom">Custom...</MenuItem>
                </Select>
              </FormControl>
              {isCustomWindow && (
                <TextField
                  fullWidth
                  type="number"
                  label="Custom window (days)"
                  value={formData.sync_window_days}
                  onChange={handleCustomWindowChange}
                  inputProps={{ min: WINDOW_MIN, max: WINDOW_MAX, step: 1 }}
                  error={!!errors.sync_window_days}
                  helperText={errors.sync_window_days || `Enter a whole number between ${WINDOW_MIN} and ${WINDOW_MAX}`}
                  sx={{ mt: 1 }}
                />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                How many days of historical data to sync each time
              </Typography>
              {!isCustomWindow && errors.sync_window_days && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errors.sync_window_days}
                </Alert>
              )}
            </Grid>

            {/* Intraday Window Days (later runs of the same UTC day) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Intraday window (later runs)"
                value={formData.intraday_window_days}
                onChange={handleIntradayWindowChange}
                inputProps={{ min: INTRADAY_MIN, max: WINDOW_MAX, step: 1 }}
                error={!!errors.intraday_window_days}
                helperText={errors.intraday_window_days || '0 = same as data window. For multi-run-per-day schedules: the FIRST run of the UTC day uses the data window; later runs use this shorter window (days).'}
              />
            </Grid>

            {/* Enable Schedule */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_enabled}
                    onChange={(e) => handleInputChange('is_enabled', e.target.checked)}
                  />
                }
                label="Schedule is enabled"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The scheduled job will run according to the cron expression specified above
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
          Update Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
}
